import type { TemplatePortfolioDraft, TemplatePortfolioExecutionItem } from "@adpilot/shared";
import {
  IconArrowRight,
  IconCircleCheck,
  IconCircleX,
  IconInfoCircle,
  IconLoader2,
  IconPlayerPause,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared/error-state";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  useEnableTemplatePortfolioExecution,
  useRetryTemplatePortfolioEnableItem,
  useTemplatePortfolioExecution,
} from "./api";
import {
  campaignTypeLabel,
  ExecutionBadge,
  ExecutionList,
  ExecutionProgressCard,
  ExecutionRow,
  failureReason,
  pinFirst,
  RowRetryButton,
} from "./execution/execution-ui";
import { TemplatePortfolioFooter } from "./TemplatePortfolioShell";

export interface StepEnableProps {
  shopId: string;
  draft: TemplatePortfolioDraft;
  executionId: string;
}

/**
 * ⑤ 一键开启 (docs/tech/12 §3 ⑤, Paper TP-13 待开启 / TP-13a 确认 / TP-14 开启中 /
 * TP-15 开启完成 / TP-15a 部分开启失败). Only created campaigns are listed.
 */
export function StepEnable({ shopId, executionId }: StepEnableProps) {
  const { t } = useTranslation("templatePortfolio");
  const query = useTemplatePortfolioExecution(shopId, executionId);
  const enable = useEnableTemplatePortfolioExecution(shopId, executionId);
  const retry = useRetryTemplatePortfolioEnableItem(shopId, executionId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [retryingAll, setRetryingAll] = useState(false);

  if (query.isPending) return <Skeleton className="h-[620px] w-full rounded-md" />;
  if (query.isError) {
    return (
      <ErrorState
        message={t("execute.loadError")}
        onRetry={() => void query.refetch()}
        isRetrying={query.isFetching}
      />
    );
  }

  const execution = query.data;
  const items = execution.items.filter((item) => item.status === "succeeded");
  const paused = items.filter((item) => item.enableStatus === "not_requested");
  const failed = items.filter((item) => item.enableStatus === "failed");
  const enabled = items.filter((item) => item.enableStatus === "enabled").length;
  const inFlight = items.length - paused.length - failed.length - enabled;
  // ponytail: a campaign created by a later ④ retry after 一键开启 stays 待开启 here; the
  // button reappears for it only once nothing is in flight.
  const phase = inFlight > 0 ? "enabling" : paused.length > 0 ? "paused" : "done";

  async function startEnable() {
    try {
      await enable.mutateAsync(undefined);
      setConfirmOpen(false);
    } catch (error) {
      toast.error(await getApiErrorMessage(error));
    }
  }

  async function retryItems(targets: TemplatePortfolioExecutionItem[]) {
    for (const item of targets) {
      try {
        await retry.mutateAsync(item.id);
      } catch (error) {
        toast.error(await getApiErrorMessage(error));
      }
    }
  }

  async function retryAll() {
    setRetryingAll(true);
    try {
      await retryItems(failed);
    } finally {
      setRetryingAll(false);
    }
  }

  const name = execution.portfolio.name;
  const portfolioId = execution.portfolio.portfolioId;
  const portfolioLink = (primary: boolean) =>
    portfolioId ? (
      <Button
        variant={primary ? "default" : "outline"}
        className={primary ? "text-xs" : "bg-surface text-xs"}
        render={<Link to="/portfolios/$id" params={{ id: portfolioId }} />}
      >
        {t("execute.toPortfolio")}
        {primary ? <IconArrowRight className="size-3.5 opacity-100" aria-hidden /> : null}
      </Button>
    ) : null;

  let card: React.ReactNode;
  let footer: React.ReactNode;
  if (phase === "paused") {
    card = (
      <ExecutionProgressCard
        variant="enable"
        caption={`${name} · ${t("enable.captionPaused")}`}
        value={String(paused.length)}
        unit={t("enable.unitPaused")}
        legend={[{ tone: "muted", count: paused.length, label: t("enable.legend.paused") }]}
      />
    );
    footer = (
      <TemplatePortfolioFooter
        hint={
          <span className="flex items-center gap-1.5 text-xs">
            <IconInfoCircle className="size-3.5 shrink-0" aria-hidden />
            {t("enable.spendHint")}
          </span>
        }
      >
        {portfolioLink(false)}
        <Button className="text-xs" onClick={() => setConfirmOpen(true)}>
          <IconPlayerPlay className="size-3.5 opacity-100" aria-hidden />
          {t("enable.enableAll", { count: paused.length })}
        </Button>
      </TemplatePortfolioFooter>
    );
  } else {
    const caption =
      phase === "enabling"
        ? t("enable.captionEnabling")
        : failed.length > 0
          ? t("enable.captionPartial")
          : t("enable.captionDone");
    const segments = [
      { tone: "success" as const, count: enabled, label: t("enable.legend.enabled") },
      { tone: "destructive" as const, count: failed.length, label: t("enable.legend.failed") },
      { tone: "info" as const, count: inFlight, label: t("enable.legend.enabling") },
    ].filter((segment) => segment.count > 0);
    card = (
      <ExecutionProgressCard
        variant="enable"
        caption={`${name} · ${caption}`}
        value={`${enabled} / ${items.length}`}
        unit={
          failed.length > 0
            ? t("enable.unitEnabledWithFailures", { count: failed.length })
            : t("enable.unitEnabled")
        }
        legend={segments}
        // The in-flight share stays an empty stretch of the track (Paper TP-14).
        bar={segments.map((segment) =>
          segment.tone === "info" ? { ...segment, tone: "track" as const } : segment,
        )}
        barTrack
      />
    );
    footer =
      phase === "enabling" ? (
        <TemplatePortfolioFooter>
          {portfolioLink(false)}
          <Button disabled className="text-xs">
            <IconLoader2 className="size-3.5 animate-spin opacity-100" aria-hidden />
            {t("enable.enablingButton")}
          </Button>
        </TemplatePortfolioFooter>
      ) : failed.length > 0 ? (
        <TemplatePortfolioFooter>
          <Button
            variant="destructive-outline"
            className="border-badge-destructive-border bg-surface text-xs"
            loading={retryingAll}
            disabled={retry.isPending}
            onClick={() => void retryAll()}
          >
            {t("execute.retryAll", { count: failed.length })}
          </Button>
          {portfolioLink(true)}
        </TemplatePortfolioFooter>
      ) : (
        <TemplatePortfolioFooter>
          <Button
            variant="outline"
            className="bg-surface text-xs"
            render={<Link to="/campaigns" />}
          >
            {t("enable.backToCampaigns")}
          </Button>
          {portfolioLink(true)}
        </TemplatePortfolioFooter>
      );
  }

  return (
    <>
      {card}
      <ExecutionList variant="enable" detailHeader={t("enable.columns.type")}>
        {pinFirst(items, (item) => item.enableStatus === "failed").map((item) => (
          <EnableRow
            key={item.id}
            item={item}
            disabled={retryingAll || (retry.isPending && retry.variables === item.id)}
            onRetry={() => void retryItems([item])}
          />
        ))}
      </ExecutionList>
      {footer}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogPopup className="max-w-[440px] rounded-[12px]">
          <AlertDialogHeader className="gap-2 px-5 pt-5 pb-4">
            <AlertDialogTitle className="font-semibold text-base text-ink leading-6">
              {t("enable.confirmTitle", { count: paused.length })}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] text-text-secondary leading-5">
              {t("enable.confirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="bg-canvas px-5 py-3">
            <AlertDialogClose
              render={<Button variant="outline" className="bg-surface text-[13px]" />}
            >
              {t("enable.confirmCancel")}
            </AlertDialogClose>
            <Button
              className="text-[13px]"
              loading={enable.isPending}
              onClick={() => void startEnable()}
            >
              <IconPlayerPlay className="size-3.5 opacity-100" aria-hidden />
              {t("enable.confirm")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}

function EnableRow({
  item,
  disabled,
  onRetry,
}: {
  item: TemplatePortfolioExecutionItem;
  disabled: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation("templatePortfolio");
  const name = item.campaignName ?? "";
  const type = campaignTypeLabel(t, item);
  switch (item.enableStatus) {
    case "enabled":
      return (
        <ExecutionRow
          variant="enable"
          icon={<IconCircleCheck className="text-success-foreground" stroke={1.8} aria-hidden />}
          name={name}
          detail={type}
          status={
            <ExecutionBadge variant="enable" tone="success">
              {t("enable.status.enabled")}
            </ExecutionBadge>
          }
        />
      );
    case "failed": {
      const reason = failureReason(item.enableErrorCode);
      return (
        <ExecutionRow
          variant="enable"
          className="bg-badge-destructive-background"
          icon={<IconCircleX className="text-destructive-foreground" stroke={1.8} aria-hidden />}
          name={name}
          detail={
            <span className="line-clamp-2" title={item.enableErrorMessage ?? undefined}>
              {item.enableErrorMessage
                ? reason
                  ? t("enable.failedWithReasonAndMessage", {
                      reason,
                      message: item.enableErrorMessage,
                    })
                  : t("enable.failedWithMessage", { message: item.enableErrorMessage })
                : reason
                  ? t("enable.failedWithReason", { reason })
                  : t("enable.failed")}
            </span>
          }
          detailClassName="text-destructive-foreground"
          status={<RowRetryButton onClick={onRetry} disabled={disabled} />}
        />
      );
    }
    case "pending":
    case "running":
      return (
        <ExecutionRow
          variant="enable"
          icon={
            <IconLoader2
              className="animate-spin text-badge-information-foreground"
              stroke={1.8}
              aria-hidden
            />
          }
          name={name}
          detail={type}
          status={
            <ExecutionBadge variant="enable" tone="info">
              {t("enable.status.enabling")}
            </ExecutionBadge>
          }
        />
      );
    default:
      return (
        <ExecutionRow
          variant="enable"
          icon={<IconPlayerPause className="text-muted-foreground" stroke={1.8} aria-hidden />}
          name={name}
          detail={type}
          status={
            <ExecutionBadge variant="enable" tone="paused">
              {t("enable.status.paused")}
            </ExecutionBadge>
          }
        />
      );
  }
}
